import { BreakpointObserver } from '@angular/cdk/layout';
import {
  Component,
  inject,
  OnInit,
  Renderer2,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router,
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { lazyComponent, updateGlobalLocale } from '@osos/shared/legacy-common';
import {
  AppStateActions,
  AppStorageService,
  AuthState,
  BreadcrumbService,
  ConfigService,
  CoreSpaceActions,
  DirtyCheckService,
  DynamicComponentFactoryService,
  FetchCurrentUserGroups,
  FetchCurrentUserProfile,
  HandleInstalledPlugins,
  LanguageState,
  PermissionsState,
  RegisterComponents,
  TypesUtilsService,
  UserPreferencesActions,
  UserPreferencesState,
} from '@osos/shared/legacy-core';
import {
  AccessRightLevelEnum,
  PrimeFlexBreakpoints,
  REFRESH_PAGE_ROUTE,
} from '@osos/shared/types';
import { ConfirmUnsavedChangesComponent } from '@osos/shared/utility';
import { PrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';
import {
  BehaviorSubject,
  combineLatest,
  concat,
  connect,
  merge,
  Observable,
} from 'rxjs';
import {
  delay,
  filter,
  finalize,
  map,
  skip,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { FONT_BASE_SIZE } from './layout/const';
import { ThemeService } from './layout/services/theme.service';
const RETURN_URL_KEY = 'auth:return-url'; // <== added

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  private appStorageService = inject(AppStorageService);

  @ViewChild('tourRef', { read: ViewContainerRef })
  private _tourContainerRef: ViewContainerRef;

  private _tourComponent$ = lazyComponent(() =>
    import('@osos/shared/utility').then((m) => m.TourComponent),
  );

  private _userSessionExpiredComponent$ = lazyComponent(() =>
    import('@osos/shared/utility').then(
      (m) => m.UserSessionExpiredModalComponent,
    ),
  );

  rtlDir$: Observable<boolean> = this.store.select(
    LanguageState.currentLanguageRTLDirection,
  );

  protected loading$: Observable<boolean>;
  private _loadingSubject = new BehaviorSubject<void>(null);

  private _appReady$: Observable<boolean>;
  private _internalPluginsReady = new BehaviorSubject<boolean>(false);

  constructor(
    private primengConfig: PrimeNG,
    private titleService: Title,
    private breadcrumbService: BreadcrumbService,
    private renderer: Renderer2,
    private store: Store,
    private router: Router,
    private themeSer: ThemeService,
    private configService: ConfigService,
    private typesUtils: TypesUtilsService,
    private transService: TranslateService,
    private dynamicComponentFactory: DynamicComponentFactoryService,
    private dialogService: DialogService,
    private dirtyCheckService: DirtyCheckService,
    private breakpointObserver: BreakpointObserver,
  ) {
    const navigationEnd$ = this.router.events.pipe(
      filter(
        (event) =>
          (event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError) &&
          (event as any).url !== `/${REFRESH_PAGE_ROUTE}`,
      ),
    );

    this._appReady$ = combineLatest([
      navigationEnd$,
      this._internalPluginsReady,
    ]).pipe(map((deps) => deps.every((dep) => !!dep)));
  }

  ngOnInit() {
    this._initPageLoading();
    this._initThemeAndUserPreferences();
    this._initLangChangeSub();
    this._initInternalPlugins();
    this._initAppTour();
    this._initUserSessionExpired();
    this._initDirtyCheckHandler();
    this._checkUserFirstLogin();
    this._handleInstalledPlugins();
  }

  private _initPageLoading(): void {
    this.loading$ = merge(
      this._loadingSubject.pipe(map(() => true)),
      this._loadingSubject.pipe(
        switchMap(() => this._appReady$.pipe(filter((ready) => !!ready))),
        // Delay when source is changed (except after the first load):
        connect((shared$) =>
          concat(shared$.pipe(take(1)), shared$.pipe(delay(200))),
        ),
        map(() => false),
      ),
    );
  }

  private _checkUserFirstLogin(): void {
    const isAuthenticated = this.store.selectSnapshot(
      AuthState.isAuthenticated,
    );

    if (!isAuthenticated) {
      this.store.dispatch(new AppStateActions.LoadAnonymousRelatedData());
      return;
    }

    const isFistLogin = this.store.selectSnapshot(
      UserPreferencesState.userFirstLogin,
    );

    const tourEnabled = this.store.selectSnapshot(
      UserPreferencesState.tourEnabled,
    );
    this.store
      .select(PermissionsState.getCurrentAdminLevel)
      .pipe(
        filter((level) => level !== null && level !== undefined),
        tap((level) => {
          const returnUrl = this.appStorageService.read(RETURN_URL_KEY);
          if (returnUrl) {
            this.appStorageService.remove(RETURN_URL_KEY);
            this.router.navigateByUrl(returnUrl, { replaceUrl: true });
            this.store.dispatch(new AppStateActions.LoadUserRelatedData());
          } else {
            if (
              isFistLogin &&
              tourEnabled &&
              level !== AccessRightLevelEnum.None
            ) {
              // Redirect to WELCOME page:

              this.router.navigate(['welcome']);
            } else {
              // Load current user related data such as data views and generic views:
              this.store.dispatch(new AppStateActions.LoadUserRelatedData());
            }
          }
        }),
        take(1),
      )
      .subscribe();
    /**
     * If it's the first login for the user, then:
     * - Redirects to welcome page, without loading the user related data (which will be loaded from welcome page).
     * - Otherwise, loads the user related data, without redirecting the user to welcome page.
     */

    this.store.dispatch(new CoreSpaceActions.MySpaces());
    this.store.dispatch(new FetchCurrentUserProfile());
    this.store.dispatch(new FetchCurrentUserGroups());
  }

  private _initThemeAndUserPreferences(): void {
    // Init user preferences:
    this.store.dispatch(new UserPreferencesActions.InitUserPreferences());

    // Get the user preferences:
    const ui = this.store.selectSnapshot(UserPreferencesState.userPreferences);

    this.themeSer.changeLayoutMode(ui.layoutMode);
    if (ui.fontSize !== FONT_BASE_SIZE) {
      this.themeSer.changeFontSize(ui.fontSize);
    }
  }

  private _initLangChangeSub(): void {
    const htmlTag = document.getElementsByTagName('html')[0] as HTMLHtmlElement;

    combineLatest([
      this.store.select(LanguageState.currentLanguage),
      this.store.select(UserPreferencesState.selectUserState),
    ])
      .pipe(
        filter(([lang, userState]) => !!lang && !!userState),
        tap(() => this._loadingSubject.next()),
        switchMap(([lang]) =>
          this.transService
            .use(lang.languageName)
            .pipe(map((translations) => ({ lang, translations }))),
        ),
        filter(({ lang, translations }) => !!translations && !!lang),
        tap(({ lang, translations }) => {
          // Change the dir attribute of html tag based on lang (rtl/ltr).
          this.renderer.setAttribute(
            htmlTag,
            'dir',
            lang.direction.toLowerCase(),
          );
          this.renderer.setAttribute(
            htmlTag,
            'lang',
            lang.languageName.toLowerCase(),
          );

          // Change global date default locale
          // Note: Used 'en' local with proper translation, to keep numbers always in English format.
          updateGlobalLocale('en', {
            relativeTime: this.transService.instant('RELATIVE_TIME'),
          });

          // Update the app title:
          this.titleService.setTitle(
            this.typesUtils.getLocaleString(this.configService.orgConfig.NAME),
          );

          // Refresh breadcrumbs:
          this.breadcrumbService.refreshBreadcrumbs();

          // Updage PrimeNG translation:
          this.primengConfig.setTranslation(translations['primeng']);
        }),
        skip(1), // Skip the first page load
      )
      .subscribe({ next: () => window.location.reload() });
  }

  private _initInternalPlugins(): void {
    merge(
      import('@osos/shared/dynamic'),
      import('@osos/shared/templates'),
      import('./shared/dynamic-widgets-components'),
      // TODO: Remove the custom plugins imports once we setup the Module Federation:
      import('./shared/custom-plugins/task-mgmt'),
      import('./shared/custom-plugins/gate'),
      import('./shared/custom-plugins/diwan'),
      import('./shared/custom-plugins'),
    )
      .pipe(
        tap((m) => {
          this.dynamicComponentFactory.registerInternalPlugin(
            m.PluginModuleConfig,
          );

          // Register the metdata of the exposed components:
          this.store.dispatch(
            new RegisterComponents(m.PluginModuleConfig.metadata),
          );
        }),
        finalize(() => this._internalPluginsReady.next(true)),
      )
      .subscribe();
  }

  private _handleInstalledPlugins(): void {
    const plugins = this.configService.orgConfig.PAGE_CUSTOM_PLUGIN;

    this.store.dispatch(new HandleInstalledPlugins(plugins));
  }

  private _initAppTour(): void {
    // Disable the tour mode on mobile:
    const isMatchedMobile = this.breakpointObserver.isMatched([
      PrimeFlexBreakpoints.XSmall,
      PrimeFlexBreakpoints.Small,
    ]);
    this.store.dispatch(
      new UserPreferencesActions.SetTourMode({ supported: !isMatchedMobile }),
    );

    this.store
      .select(UserPreferencesState.tourEnabled)
      .pipe(
        withLatestFrom(this.store.select(UserPreferencesState.tourSupported)),
        filter(([enabled, supported]) => !!supported && !!enabled),
        switchMap(() => this._tourComponent$),
        tap((cmp) => this._tourContainerRef.createComponent(cmp)),
        take(1),
      )
      .subscribe();
  }

  private _initUserSessionExpired(): void {
    this.store
      .select(AuthState.userSessionExpired)
      .pipe(
        filter((expired) => expired),
        switchMap(() => this._userSessionExpiredComponent$),
      )
      .subscribe((cmp) => {
        this.dialogService.open(cmp, {
          closeOnEscape: false,
          closable: false,
          showHeader: false,
          width: '500px',
        });
      });
  }

  private _initDirtyCheckHandler(): void {
    this.dirtyCheckService.pendingUnsavedChangesToConfirm$
      .pipe(
        switchMap(() => {
          const dialogRef = this.dialogService.open(
            ConfirmUnsavedChangesComponent,
            { width: '500px', modal: true, showHeader: false },
          );
          return dialogRef.onClose;
        }),
      )
      .subscribe((confirmed) => {
        this.dirtyCheckService.confirmUnsavedChanges(!!confirmed);
      });
  }
}
